import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Course, Enrollment } from '@/types/models';
import { useReveal } from '@/lib/hooks/useReveal';
import { useAuth } from '@/features/auth/useAuth';
import { fetchCourses, fetchEnrollment } from '../api';

// ── Course theming ──────────────────────────────────────────────────────────
// Deterministic, curated palette per slug. Falls back to a hash-based pick.
type Theme = { from: string; to: string; ring: string; emoji: string };

const THEMES: Record<string, Theme> = {
  gamification: {
    from: '#5aba84',
    to: '#0f4d27',
    ring: 'ring-emerald-200',
    emoji: '🎮',
  },
};

const FALLBACK_THEMES: Theme[] = [
  { from: '#5aba84', to: '#277a4c', ring: 'ring-emerald-200', emoji: '📚' },
  { from: '#5b8def', to: '#1e3a8a', ring: 'ring-blue-200', emoji: '💡' },
  { from: '#f59e0b', to: '#7c2d12', ring: 'ring-amber-200', emoji: '🚀' },
  { from: '#ec4899', to: '#831843', ring: 'ring-pink-200', emoji: '🎯' },
  { from: '#8b5cf6', to: '#4c1d95', ring: 'ring-violet-200', emoji: '✨' },
];

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function themeFor(slug: string): Theme {
  return THEMES[slug] ?? FALLBACK_THEMES[hash(slug) % FALLBACK_THEMES.length]!;
}

// ── Component ───────────────────────────────────────────────────────────────

type Filter = 'all' | 'in-progress' | 'not-started';

export function CoursesPage() {
  const { user } = useAuth();
  const [courses, setCourses] = useState<Course[] | null>(null);
  const [enrollments, setEnrollments] = useState<Record<string, Enrollment | null>>({});
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  useReveal();

  useEffect(() => {
    fetchCourses()
      .then((cs) => {
        setCourses(cs);
        if (user) {
          // Fan-out enrollment lookups. Courses are few; fine for now.
          cs.forEach((c) => {
            void fetchEnrollment(c.id)
              .then((e) => setEnrollments((prev) => ({ ...prev, [c.id]: e })))
              .catch(() => setEnrollments((prev) => ({ ...prev, [c.id]: null })));
          });
        }
      })
      .catch((e) => setError(String(e)));
  }, [user]);

  const visible = useMemo(() => {
    if (!courses) return null;
    const q = query.trim().toLowerCase();
    return courses.filter((c) => {
      const matchesQuery =
        !q || c.title.toLowerCase().includes(q) || c.summary.toLowerCase().includes(q);
      if (!matchesQuery) return false;
      if (filter === 'all') return true;
      const enr = enrollments[c.id];
      if (filter === 'in-progress') return !!enr && enr.progress < 1;
      if (filter === 'not-started') return !enr;
      return true;
    });
  }, [courses, query, filter, enrollments]);

  if (error) return <p className="text-red-600">{error}</p>;

  const inProgressCount = Object.values(enrollments).filter((e) => e && e.progress < 1).length;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Hero */}
      <div className="reveal mb-8 rounded-3xl bg-gradient-to-br from-brand-500 to-brand-700 p-8 text-white">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-brand-100/90">
              Course Library
            </p>
            <h1 className="font-serif text-4xl font-bold mt-1">Pick your next adventure</h1>
            <p className="text-brand-50/90 text-base mt-2 max-w-xl leading-relaxed">
              Bite-sized lessons, real interactivity, and a per-course leaderboard. Earn XP as you go.
            </p>
          </div>
          {user && (
            <div className="flex gap-3">
              <Stat label="In progress" value={inProgressCount} highlight />
            </div>
          )}
        </div>
      </div>

      {/* Search + filters */}
      <div className="reveal mb-8 flex flex-wrap items-center gap-3" style={{ animationDelay: '60ms' }}>
        <div className="relative flex-1 min-w-[220px]">
          <SearchIcon />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search courses…"
            className="w-full rounded-full border border-[#eeeeee] bg-white pl-11 pr-4 py-3 text-sm focus:border-brand-400 focus:outline-none focus:ring-4 focus:ring-brand-100 transition"
          />
        </div>
        {user && (
          <div className="flex gap-1.5 rounded-full bg-[#f5f5f5] p-1">
            <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>All</FilterChip>
            <FilterChip active={filter === 'in-progress'} onClick={() => setFilter('in-progress')}>In progress</FilterChip>
            <FilterChip active={filter === 'not-started'} onClick={() => setFilter('not-started')}>Not started</FilterChip>
          </div>
        )}
      </div>

      {/* Grid */}
      {!visible ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-72 rounded-2xl bg-[#f5f5f5] animate-pulse" />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#eeeeee] py-16 text-center">
          <p className="text-2xl mb-1">🔍</p>
          <p className="text-[#aaaaaa] text-sm">
            {query ? `No courses match "${query}"` : 'No courses match this filter.'}
          </p>
        </div>
      ) : (
        <ul className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((c, i) => {
            const enr = enrollments[c.id];
            return (
              <li key={c.id} className="reveal" style={{ animationDelay: `${i * 60}ms` }}>
                <CourseCard course={c} enrollment={enr ?? null} />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ── Bits ────────────────────────────────────────────────────────────────────

function Stat({ label, value, highlight = false }: { label: string; value: string | number; highlight?: boolean }) {
  return (
    <div
      className={`rounded-xl px-4 py-2 text-center ${highlight ? 'bg-white/20 ring-1 ring-white/30' : 'bg-white/10'}`}
    >
      <div className="font-display text-xl font-extrabold leading-tight">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-brand-50/80">{label}</div>
    </div>
  );
}

function SearchIcon() {
  return (
    <svg
      className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#aaaaaa]"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-all ${
        active
          ? 'bg-white text-brand-700'
          : 'text-[#555555] hover:text-[#1a1a1a]'
      }`}
    >
      {children}
    </button>
  );
}

function CourseCard({ course: c, enrollment }: { course: Course; enrollment: Enrollment | null }) {
  const theme = themeFor(c.slug);
  const progressPct = enrollment ? Math.round(enrollment.progress * 100) : 0;
  const isComplete = enrollment && enrollment.progress >= 1;
  const isStarted = enrollment && enrollment.progress < 1;

  return (
    <Link to={`/courses/${c.slug}`} className="group block h-full">
      <div className="relative h-full overflow-hidden rounded-2xl border border-[#eeeeee] bg-white transition-all duration-300 group-hover:-translate-y-1">
        {/* Themed header banner */}
        <div
          className="relative h-28 overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${theme.from}, ${theme.to})` }}
        >
          <div className="absolute inset-0 opacity-25 mix-blend-overlay" style={{
            background: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.4), transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.3), transparent 50%)',
          }} />
          <div className="absolute right-4 top-4 text-5xl drop-shadow-md transition-transform duration-500 group-hover:rotate-12 group-hover:scale-110">
            {theme.emoji}
          </div>
          {isComplete && (
            <div className="absolute left-4 top-4 rounded-full bg-white/95 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-brand-700">
              ✓ Complete
            </div>
          )}
          {isStarted && !isComplete && (
            <div className="absolute left-4 top-4 rounded-full bg-white/95 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-brand-700">
              {progressPct}% Done
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex h-[calc(100%-7rem)] flex-col p-5">
          <h2 className="font-serif text-lg font-bold text-[#1a1a1a] mb-2 leading-snug line-clamp-2">
            {c.title}
          </h2>
          <p className="text-sm text-[#555555] leading-relaxed line-clamp-3 flex-1">{c.summary}</p>

          {/* Progress bar (if enrolled) */}
          {enrollment && !isComplete && (
            <div className="mt-3 mb-1">
              <div className="h-1.5 overflow-hidden rounded-full bg-[#f0f0f0]">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${progressPct}%`,
                    background: `linear-gradient(90deg, ${theme.from}, ${theme.to})`,
                  }}
                />
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-4 flex items-center justify-between border-t border-[#f5f5f5] pt-3">
            <div className="flex items-center gap-3 text-xs text-[#aaaaaa]">
              <span className="inline-flex items-center gap-1">
                <span>📖</span>
                <span>{c.lessonCount} {c.lessonCount === 1 ? 'lesson' : 'lessons'}</span>
              </span>
              <span className="inline-flex items-center gap-1">
                <span>⚡</span>
                <span>{c.lessonCount * 100} XP</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
